import React, {useEffect, useState} from "react";
import ReactDOM from 'react-dom'
import PropTypes from "prop-types";
import Button from "../Button/Button";

const Popup = (props) => {
  const {
    trigger,
    children,
    buttons,
    postButtons,
    title = '',
    hideOnOutsideClick = true,
    showCloseButton = true,
    closeButtonLabel = 'Sluiten',
    closeButtonClassName = 'btn',
    disableButtons = false,
    showPopup = false,
    setShowPopup,
    iteration = false,
    callbackOpenPopup,
  } = props

  const portalContainer = typeof document !== 'undefined' ? document.getElementById("portal-container") : null;
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // match this to your CSS transition-duration (ms)
  const TRANSITION_MS = 250;

  // helper to read prop (bool or keyed)
  const showPopupBool = () => {
    return (
      (iteration !== false && showPopup && showPopup[iteration]) ||
      (iteration === false && !!showPopup)
    )
  }

  // Sync internal mount/open state to prop
  useEffect(() => {
    const shouldShow = !!showPopupBool();

    if (shouldShow) {
      // mount then open next frame (so CSS transition runs)
      setIsMounted(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setIsOpen(true)));
      if (callbackOpenPopup) callbackOpenPopup();
      return;
    }

    // if prop says hide and we are mounted -> run exit animation then unmount
    if (!shouldShow && isMounted) {
      setIsOpen(false);
      const t = setTimeout(() => {
        setIsMounted(false);
      }, TRANSITION_MS + 20);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopup, iteration]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        // trigger parent's setter so app state is consistent
        setShowPopupBool(false)
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  /**
   * handle setShowPopup in correct prototype showPopup:
   * showPopup can be bool or object/array.
   *
   * @param bool
   */
  const setShowPopupBool = (bool) => {
    if (iteration !== false) {
      // keep parent state shape
      setShowPopup({...showPopup, [iteration]: bool})
    } else {
      setShowPopup(bool)
    }

    // callback (note: parent will also update prop -> effect will handle animation)
    if (callbackOpenPopup && bool) {
      callbackOpenPopup()
    }
  }

  const onClickOutside = () => {
    if (hideOnOutsideClick) {
      setShowPopupBool(false)
    }
  }

  // nothing to render if portal missing or not mounted
  if (!portalContainer || !isMounted) return (
    <>
      {trigger && React.cloneElement(trigger, { onClick: () => setShowPopupBool(true) })}
    </>
  );

  return (
    <div className="component-popup">
      {trigger &&
        <div className="popup-trigger">
          {React.cloneElement(trigger, {
            onClick: () => setShowPopupBool(true),
          })}
        </div>
      }

      {ReactDOM.createPortal(
        <>
          <div
            className={`popup-backdrop ${isOpen ? 'open' : ''}`}
            onClick={onClickOutside}
          />
          <div className={`popup-modal ${isOpen ? 'open' : ''}`}>
            <div className="modal">
              <div className="scroll-box">
                <h1 className="title">{title}</h1>
                <div className="content">
                  {children}
                </div>
              </div>
              <div className="actions">
                {buttons && buttons}
                {showCloseButton &&
                  <Button
                    label={closeButtonLabel}
                    onClick={() => setShowPopupBool(false)}
                    className={closeButtonClassName}
                    disabled={disableButtons}
                  />
                }
                {postButtons && postButtons}
              </div>
            </div>
          </div>
        </>,
        portalContainer
      )}
    </div>
  )
}

Popup.propTypes = {
  title: PropTypes.string,
  closeButtonLabel: PropTypes.string,
  closeButtonClassName: PropTypes.string,
  showCloseButton: PropTypes.bool,
  disableButtons: PropTypes.bool,
  hideOnOutsideClick: PropTypes.bool,
  buttons: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  postButtons: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  trigger: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  iteration: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  showPopup: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]).isRequired,
  setShowPopup: PropTypes.func.isRequired,
  callbackOpenPopup: PropTypes.func,
}
export default Popup
